import { api } from './api';

/** Server-side caps (mirror SUPPORT_TICKET_* in api/src/middleware/s3Upload.ts). */
export const SUPPORT_TICKET_MAX_IMAGES = 5;
export const SUPPORT_TICKET_MAX_VIDEOS = 1;
export const SUPPORT_TICKET_IMAGE_MAX_MB = 15;
export const SUPPORT_TICKET_VIDEO_MAX_MB = 100;
export const SUPPORT_TICKET_VIDEO_MAX_BYTES = SUPPORT_TICKET_VIDEO_MAX_MB * 1024 * 1024;

const VIDEO_URL_RE = /\.(mp4|webm|mov|mkv|3gp|avi|mpeg|mpg|m4v)(\?.*)?$/i;
/** Ticket attachments share one URL list; a video is told apart by its extension. */
export const isVideoUrl = (url: string): boolean => VIDEO_URL_RE.test(url);

export interface CreateTicketPayload {
  subject: string;
  description: string;
  category?: string;
  priority?: string;
  /** Already-hosted URLs (JSON path). Ignored when files are given. */
  screenshots?: string[];
}

export interface TicketFiles {
  images?: File[];
  video?: File | null;
}

/**
 * Build a multipart body: scalar fields as strings, images then the video under
 * `field`. The API accepts JSON when there are no files, so callers only pay
 * the multipart cost when they actually attach something.
 */
function toMultipart(fields: object, field: string, files: TicketFiles): FormData {
  const fd = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value === undefined || value === null || Array.isArray(value)) return;
    fd.append(key, String(value));
  });
  (files.images || []).forEach((file) => fd.append(field, file));
  if (files.video) fd.append(field, files.video);
  return fd;
}

const hasFiles = (files?: TicketFiles) => !!files && ((files.images?.length ?? 0) > 0 || !!files.video);

// A 100 MB recording on a shop's mobile data link easily outlives the client's
// default 30 s timeout, so multipart requests get a 10-minute ceiling instead.
const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 10 * 60 * 1000 };

export const supportTicketApi = {
  create: async (data: CreateTicketPayload, files?: TicketFiles) => {
    const res = hasFiles(files)
      ? await api.post('/support-tickets', toMultipart(data, 'screenshots', files!), MULTIPART)
      : await api.post('/support-tickets', data);
    return res.data.data;
  },

  list: async (params?: { page?: number; limit?: number; status?: string; search?: string }) => {
    const res = await api.get('/support-tickets', { params });
    return res.data.data;
  },

  getById: async (id: string) => {
    const res = await api.get(`/support-tickets/${id}`);
    return res.data.data;
  },

  addMessage: async (id: string, data: { message: string; attachments?: string[] }, files?: TicketFiles) => {
    const res = hasFiles(files)
      ? await api.post(`/support-tickets/${id}/messages`, toMultipart(data, 'attachments', files!), MULTIPART)
      : await api.post(`/support-tickets/${id}/messages`, data);
    return res.data.data;
  },

  close: async (id: string) => {
    const res = await api.patch(`/support-tickets/${id}/close`);
    return res.data.data;
  },
};
